/**
 * AppLayout - root authenticated layout shell.
 *
 * Structure:
 *   <div> (flex row)
 *     <Sidebar />
 *     <div> (flex column)
 *       <TopBar />
 *       <main> (scroll root)
 *         <div max-width container>
 *           <motion.div key=pathname pageTransition>
 *             <Outlet />
 *
 * Page transition: entrance-only fadeInUp keyed on pathname so each route
 * mount replays the entrance. We deliberately do NOT wrap in AnimatePresence
 * with mode="wait" + exit animation. That combination races under React 19
 * StrictMode + framer-motion v12, where the exit-complete callback can fail
 * to fire and leave the new motion.div stuck at opacity:0 (manifests as a
 * blank page on back navigation from nested routes like /analysis/:ticker).
 *
 * Respects useReducedMotion().
 *
 * Do not add em-dashes anywhere.
 */

import { useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import Sidebar from './Sidebar'
import { TopBar } from './TopBar'
import CommandPalette from './CommandPalette'
import { motion as motionTokens, color } from '@/design-system/tokens'

const pageTransitionTransition = {
  duration: motionTokens.duration.fast / 1000,
  ease: motionTokens.ease.entrance,
}

export default function AppLayout() {
  const { pathname } = useLocation()
  const shouldReduceMotion = useReducedMotion()
  // Scroll root is <main> (overflowY: auto), NOT window. TopBar reads this
  // ref via useScroll({ container }) so scroll-collapse fires on the real
  // scroll source.
  const mainRef = useRef<HTMLElement | null>(null)

  return (
    <div
      data-slot="app-layout"
      style={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        width: '100%',
        maxWidth: 'none',
        minHeight: '100vh',
        background: color.blackRich,
      }}
    >
      <Sidebar />

      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <TopBar scrollContainer={mainRef} />

        <main
          ref={mainRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <div
            style={{
              width: '100%',
              padding: '24px',
            }}
          >
            <motion.div
              key={pathname}
              initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
              animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={shouldReduceMotion ? { duration: 0 } : pageTransitionTransition}
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>

      <CommandPalette />
    </div>
  )
}
